// src/features/invoices/hooks/useInvoices.ts

import { useState, useCallback, useEffect } from 'react';
import { invoiceService } from '../../../services/invoice/invoiceService';
import { Invoice, InvoiceFilter, InvoiceLineItemInput } from '../types';

export const useInvoices = () => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);

  // State für die zuletzt festgeschriebene Rechnung des gleichen Kunden.
  // Wird erst geladen, wenn die Detailansicht eines Entwurfs die Sektion
  // "Letzte Rechnung an diesen Kunden" aufklappt – nicht beim Öffnen des
  // Entwurfs selbst, damit unnötige Queries vermieden werden.
  const [previousInvoice, setPreviousInvoice] = useState<Invoice | null>(null);
  const [previousInvoiceLoading, setPreviousInvoiceLoading] = useState(false);
  const [previousInvoiceLoaded, setPreviousInvoiceLoaded] = useState(false);

  // Default-Filter: Status "Entwurf" + Vormonat (analog zur DATEV-Export-Logik)
  // Jahresübergang bei Januar wird berücksichtigt.
  const [filter, setFilter] = useState<InvoiceFilter>(() => {
    const now = new Date();
    const isJanuary = now.getMonth() === 0;
    const month = isJanuary ? 12 : now.getMonth(); // getMonth() ist 0-basiert; Vormonat = aktueller Index als 1-basierter Wert
    const year = isJanuary ? now.getFullYear() - 1 : now.getFullYear();
    return { status: 'entwurf', year, month };
  });

  const loadInvoices = useCallback(async () => {
    setLoading(true);
    try {
      // Status-Filter wird client-seitig im Dashboard angewendet, damit die
      // Status-Tab-Counts immer korrekt für den gewählten Monat sind.
      // Server-seitig filtern wir nur nach Monat/Jahr.
      const data = await invoiceService.getInvoices({
        year: filter.year,
        month: filter.month
      });
      setInvoices(data);
    } catch (error) {
      console.error('Fehler beim Laden der Rechnungen:', error);
    } finally {
      setLoading(false);
    }
  }, [filter.year, filter.month]);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  const selectInvoice = async (invoiceId: string) => {
    // Vorgänger-State nur dann verwerfen, wenn wir tatsächlich auf eine
    // andere Rechnung wechseln. Bei einem Reload derselben Rechnung
    // (z.B. nach Position-Edit/-Delete) bleibt der Vorgänger-Cache erhalten,
    // damit die aufgeklappte Sektion nicht in den Lade-Zustand zurückfällt.
    const isSwitchingInvoice = selectedInvoice?.id !== invoiceId;
    let invoice = await invoiceService.getInvoice(invoiceId);

    // Für Entwürfe: falls die Positionsnummern Lücken aufweisen (Altlast aus
    // einem Zustand vor dem Renumber-beim-Löschen-Deploy), lückenlos
    // umnummerieren und neu laden. Festgeschriebene Rechnungen werden nicht
    // angefasst.
    if (invoice && invoice.status === 'entwurf') {
      const items = invoice.line_items || [];
      if (items.length > 0) {
        const sorted = [...items].sort((a, b) => a.position - b.position);
        const hasGaps = sorted.some((item, idx) => item.position !== idx + 1);
        if (hasGaps) {
          const renumberResult = await invoiceService.renumberLineItemsSequentially(invoiceId);
          if (!renumberResult.success) {
            console.error('[useInvoices] Renumbering beim Öffnen des Entwurfs fehlgeschlagen:', renumberResult.error);
          } else {
            invoice = await invoiceService.getInvoice(invoiceId);
          }
        }
      }
    }

    setSelectedInvoice(invoice);
    if (isSwitchingInvoice) {
      setPreviousInvoice(null);
      setPreviousInvoiceLoaded(false);
      setPreviousInvoiceLoading(false);
    }
  };

  const closeDetail = () => {
    setSelectedInvoice(null);
    setPreviousInvoice(null);
    setPreviousInvoiceLoaded(false);
    setPreviousInvoiceLoading(false);
  };

  /**
   * Lädt die zuletzt festgeschriebene Rechnung des Kunden der aktuell
   * geöffneten Rechnung. Wird vom Dashboard beim Aufklappen der
   * "Letzte Rechnung"-Sektion aufgerufen. Mehrfachaufrufe sind kostenlos,
   * weil die Funktion über previousInvoiceLoaded selbst entscheidet, ob
   * sie erneut abfragen muss.
   */
  const loadPreviousInvoice = async () => {
    if (!selectedInvoice || !selectedInvoice.customer_id) return;
    if (previousInvoiceLoading || previousInvoiceLoaded) return;

    setPreviousInvoiceLoading(true);
    try {
      const prev = await invoiceService.getLastFinalizedInvoiceForCustomer(
        selectedInvoice.customer_id,
        selectedInvoice.id
      );
      setPreviousInvoice(prev);
      setPreviousInvoiceLoaded(true);
    } finally {
      setPreviousInvoiceLoading(false);
    }
  };

  const addLineItem = async (invoiceId: string, item: InvoiceLineItemInput) => {
    const result = await invoiceService.addLineItem(invoiceId, item);
    if (result) {
      // Rechnung neu laden um aktualisierte Beträge zu sehen
      await selectInvoice(invoiceId);
      await loadInvoices();
    }
    return result;
  };

  const updateLineItem = async (itemId: string, updates: Partial<InvoiceLineItemInput>) => {
    const success = await invoiceService.updateLineItem(itemId, updates);
    if (success && selectedInvoice) {
      await selectInvoice(selectedInvoice.id);
      await loadInvoices();
    }
    return success;
  };

  const deleteLineItem = async (itemId: string, invoiceId: string) => {
    const success = await invoiceService.deleteLineItem(itemId, invoiceId);
    if (success) {
      await selectInvoice(invoiceId);
      await loadInvoices();
    }
    return success;
  };

  const previewPdf = async (invoiceId: string) => {
    return await invoiceService.previewPdf(invoiceId);
  };

  const approveAndSend = async (invoiceId: string) => {
    const result = await invoiceService.approveAndSend(invoiceId);
    if (result.success) {
      await loadInvoices();
      if (selectedInvoice?.id === invoiceId) {
        await selectInvoice(invoiceId);
      }
    }
    return result;
  };

  const approveInvoice = async (invoiceId: string) => {
    const result = await invoiceService.approveInvoice(invoiceId);
    if (result.success) {
      await loadInvoices();
      if (selectedInvoice?.id === invoiceId) {
        await selectInvoice(invoiceId);
      }
    }
    return result;
  };

  const deleteDraft = async (invoiceId: string) => {
    const success = await invoiceService.deleteDraft(invoiceId);
    if (success) {
      if (selectedInvoice?.id === invoiceId) {
        setSelectedInvoice(null);
      }
      await loadInvoices();
    }
    return success;
  };

  return {
    invoices,
    loading,
    filter,
    setFilter,
    loadInvoices,
    selectedInvoice,
    selectInvoice,
    closeDetail,
    addLineItem,
    updateLineItem,
    deleteLineItem,
    approveInvoice,
    approveAndSend,
    previewPdf,
    deleteDraft,
    previousInvoice,
    previousInvoiceLoading,
    previousInvoiceLoaded,
    loadPreviousInvoice
  };
};
